/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { FormattedMessage } from 'react-intl'
import { omit } from 'lodash'

import type { AsyncRequestState, FoundUser, StackDeployment } from '@modules/ui-types'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import { updateIn } from '@/lib/immutability-helpers'

import DangerButton from '../../DangerButton'

import type { FunctionComponent } from 'react'

type Props = {
  user: FoundUser
  deployment: StackDeployment
  updateMetadata: (params: {
    deployment: StackDeployment
    updater: (metadata: { [key: string]: any }) => { [key: string]: any }
    requestMeta?: { [key: string]: any }
  }) => void
  setDeploymentResourceMetadataRequest: AsyncRequestState
}

const deleteUserMetadataAction = `delete-user`

const DeleteTempUser: FunctionComponent<Props> = ({
  user,
  deployment,
  updateMetadata,
  setDeploymentResourceMetadataRequest,
}) => {
  const updateRequestIsRelevant =
    setDeploymentResourceMetadataRequest.meta.action === deleteUserMetadataAction
  const activeDeletedItem =
    updateRequestIsRelevant && setDeploymentResourceMetadataRequest.meta.username === user.username

  const spinning =
    setDeploymentResourceMetadataRequest.inProgress && updateRequestIsRelevant && activeDeletedItem

  const disabled = setDeploymentResourceMetadataRequest.inProgress && !activeDeletedItem

  const deleteUser = () =>
    updateMetadata({
      deployment,
      requestMeta: { action: deleteUserMetadataAction, username: user.username },
      updater: (metadata) =>
        updateIn(metadata, [`shield`, `found_users`], (users: { [key: string]: FoundUser }) =>
          omit(users, user.username),
        ),
    })

  return (
    <PermissionsGate
      permissions={[
        {
          type: 'deployment',
          action: 'update',
          id: deployment.id,
        },
      ]}
    >
      {({ hasPermissions }) => (
        <DangerButton
          data-test-id='confirm-to-delete-button'
          modal={{
            title: (
              <FormattedMessage
                id='cluster-manage-delete-temp-user.confirm-to-delete'
                defaultMessage='Confirm to delete'
              />
            ),
          }}
          size='s'
          onConfirm={deleteUser}
          disabled={disabled || !hasPermissions}
          isBusy={spinning}
        >
          {activeDeletedItem && setDeploymentResourceMetadataRequest.error != null ? (
            <FormattedMessage
              id='cluster-manage-delete-temp-user.delete-user-failed'
              defaultMessage='Delete failed, try again'
            />
          ) : (
            <FormattedMessage
              id='cluster-manage-delete-temp-user.delete-user'
              defaultMessage='Delete'
            />
          )}
        </DangerButton>
      )}
    </PermissionsGate>
  )
}

export default DeleteTempUser
